import React, { useState, useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TextField, Button, Chip, InputAdornment, IconButton, TablePagination, Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import GetAppIcon from '@material-ui/icons/GetApp'
import RefreshIcon from '@material-ui/icons/Refresh'
import { CSVLink } from 'react-csv'
import moment from 'moment'
import InfoIcon from '@material-ui/icons/Info'
import {
    DEVICE_TYPE_COLORS, formatNameList, namesToPlainText, formatToIST, DeviceInfoPopup
} from './activityHelpers'
import { setLoading, setSnackInfo } from '../../redux/actions/appAction'
import HttpService from '../../services/httpService'

const NAVY = '#1C375D'

const useStyles = makeStyles(() => ({
    filters: {
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 16px', background: '#fafbfd',
        border: '1px solid #e4e8ef', borderRadius: 10, marginBottom: 12
    },
    search: { minWidth: 260, flex: '1 1 260px' },
    date: { width: 170 },
    headCell: { background: '#eef2f7', color: NAVY, fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' },
    cell: { fontSize: 13 },
    path: { fontFamily: 'monospace', fontSize: 12, color: '#b4501f' },
    url: { fontFamily: 'monospace', fontSize: 11, color: '#93a1b5' },
    empty: { padding: 50, textAlign: 'center', color: '#8d9bb0' },
    bar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' },
    total: { fontWeight: 600, color: NAVY, fontSize: 14, paddingLeft: 4 },
}))

const CSV_HEADERS = [
    { label: 'Sl. No.', key: 'serialNo' },
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Experience Center', key: 'experienceCenter' },
    { label: 'City', key: 'city' },
    { label: 'Roles', key: 'roles' },
    { label: 'Device Info', key: 'deviceInfo' },
    { label: 'Page blocked', key: 'path' },
    { label: 'URL opened', key: 'url' },
    { label: 'Attempted At', key: 'time' },
    { label: 'IP Address', key: 'ipAddress' },
]

const RouteAccessLogs = (props) => {
    const classes = useStyles()

    const [logs, setLogs] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(100)

    const [search, setSearch] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')

    const [csvData, setCsvData] = useState([])
    const csvRef = useRef(null)

    const fetchLogs = useCallback(async () => {
        props.setLoading(true)
        try {
            const res = await HttpService.getDeniedRouteLogs({
                page: page + 1,
                limit: rowsPerPage,
                search: appliedSearch || undefined,
                from: from || undefined,
                to: to || undefined,
            })
            setLogs(res.data.logs || [])
            setTotal(res.data.total || 0)
        } catch (error) {
            props.setSnackInfo('Could not load route access logs', 'error')
        }
        props.setLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, appliedSearch, from, to])

    useEffect(() => { fetchLogs() }, [fetchLogs])

    const applySearch = () => {
        setPage(0)
        setAppliedSearch(search.trim())
    }

    const clearFilters = () => {
        setSearch('')
        setAppliedSearch('')
        setFrom('')
        setTo('')
        setPage(0)
    }

    const downloadCsv = async () => {
        props.setLoading(true)
        try {
            const res = await HttpService.getDeniedRouteLogs({
                page: 1,
                limit: 500,
                search: appliedSearch || undefined,
                from: from || undefined,
                to: to || undefined,
            })
            setCsvData((res.data.logs || []).map((log, index) => ({
                serialNo: index + 1,
                name: log.userName || log.userId?.name || 'N/A',
                email: log.userEmail || log.userId?.email || 'N/A',
                experienceCenter: namesToPlainText(log.userId?.experienceCenterId),
                city: namesToPlainText(log.userId?.locationId),
                roles: (log.roleNames || []).join(', '),
                deviceInfo: [
                    log.deviceInfo?.deviceType || 'desktop',
                    log.deviceInfo?.osName || 'N/A',
                    log.deviceInfo?.browserName || 'N/A',
                ].join(' | '),
                path: log.path,
                url: log.url || '',
                time: formatToIST(log.createdAt),
                ipAddress: log.ipAddress || '',
            })))
            setTimeout(() => csvRef.current && csvRef.current.link.click(), 300)
        } catch (error) {
            props.setSnackInfo('Could not export logs', 'error')
        }
        props.setLoading(false)
    }

    const hasFilter = !!(appliedSearch || from || to)

    return (
        <div>
            <CSVLink
                headers={CSV_HEADERS}
                data={csvData}
                filename={`route_access_logs_${moment().format('DD-MM-YYYY')}.csv`}
                ref={csvRef}
                style={{ display: 'none' }}
            />

            <div className={classes.filters}>
                <TextField
                    className={classes.search}
                    size="small"
                    variant="outlined"
                    placeholder="Search by name, email or page..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: '#9aa7ba', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => { setSearch(''); setAppliedSearch(''); setPage(0) }}>
                                    <ClearIcon style={{ fontSize: 16 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                    }}
                />
                <TextField
                    className={classes.date}
                    size="small"
                    variant="outlined"
                    type="date"
                    label="From"
                    value={from}
                    onChange={(e) => { setFrom(e.target.value); setPage(0) }}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    className={classes.date}
                    size="small"
                    variant="outlined"
                    type="date"
                    label="To"
                    value={to}
                    onChange={(e) => { setTo(e.target.value); setPage(0) }}
                    InputLabelProps={{ shrink: true }}
                />
                <Button variant="contained" color="primary" size="medium" onClick={applySearch}>
                    Search
                </Button>
                {hasFilter && (
                    <Button size="medium" onClick={clearFilters}>Clear</Button>
                )}
                <Tooltip title="Reload">
                    <IconButton size="small" onClick={fetchLogs}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Button
                    style={{ marginLeft: 'auto' }}
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    onClick={downloadCsv}
                    disabled={!total}
                >
                    Download CSV
                </Button>
            </div>

            <div className={classes.bar}>
                <div className={classes.total}>Total Records Found : {total}</div>
                <TablePagination
                    rowsPerPageOptions={[100, 200, 500]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={(_e, p) => setPage(p)}
                    onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                />
            </div>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.headCell}>S.No</TableCell>
                            <TableCell className={classes.headCell}>Name</TableCell>
                            <TableCell className={classes.headCell}>Email</TableCell>
                            <TableCell className={classes.headCell}>Experience Center</TableCell>
                            <TableCell className={classes.headCell}>City</TableCell>
                            <TableCell className={classes.headCell}>Device Info</TableCell>
                            <TableCell className={classes.headCell}>Page blocked</TableCell>
                            <TableCell className={classes.headCell}>Activity Log</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!logs.length && (
                            <TableRow>
                                <TableCell colSpan={8} className={classes.empty}>
                                    {hasFilter
                                        ? 'No blocked attempts match this filter'
                                        : 'No blocked attempts recorded yet'}
                                </TableCell>
                            </TableRow>
                        )}
                        {logs.map((log, index) => {
                            const device = log.deviceInfo || {}
                            const deviceType = device.deviceType || 'desktop'
                            return (
                                <TableRow key={log._id} hover>
                                    <TableCell className={classes.cell}>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell className={classes.cell}>{log.userName || log.userId?.name || 'N/A'}</TableCell>
                                    <TableCell className={classes.cell}>{log.userEmail || log.userId?.email || 'N/A'}</TableCell>
                                    <TableCell className={classes.cell}>{formatNameList(log.userId?.experienceCenterId)}</TableCell>
                                    <TableCell className={classes.cell}>{formatNameList(log.userId?.locationId)}</TableCell>
                                    <TableCell className={classes.cell}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Chip size="small" label={deviceType} style={DEVICE_TYPE_COLORS[deviceType] || DEVICE_TYPE_COLORS.desktop} />
                                            <span>{device.osName || 'N/A'} | {device.browserName || 'Browser'}</span>
                                            <Tooltip title={<DeviceInfoPopup deviceInfo={device} />} placement="right" interactive>
                                                <InfoIcon fontSize="small" style={{ cursor: 'pointer', color: '#888' }} />
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        <div className={classes.path}>{log.path}</div>
                                        {log.url && log.url !== log.path && (
                                            <div className={classes.url}>{log.url}</div>
                                        )}
                                    </TableCell>
                                    <TableCell className={classes.cell} style={{ whiteSpace: 'nowrap' }}>
                                        <strong>{formatToIST(log.createdAt)}</strong>
                                        <Tooltip
                                            placement="left"
                                            interactive
                                            title={
                                                <div style={{ fontSize: 13, padding: 6 }}>
                                                    <strong>Blocked page attempt</strong><br /><br />
                                                    <div><strong>Page:</strong> {log.path}</div>
                                                    <div><strong>URL opened:</strong> {log.url || 'N/A'}</div>
                                                    <div><strong>IP Address:</strong> {log.ipAddress || 'N/A'}</div>
                                                    <div><strong>Roles at that time:</strong> {(log.roleNames || []).join(', ') || 'N/A'}</div>
                                                </div>
                                            }
                                        >
                                            <InfoIcon fontSize="small" style={{ marginLeft: 6, cursor: 'pointer', color: '#888' }} />
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}

export default connect(null, { setLoading, setSnackInfo })(RouteAccessLogs)
